import React, { useCallback, useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";

import AcoesQrMesa from "./AcoesQrMesa";
import LinkDaMesa from "./LinkDaMesa";
import { gravarNomeQr, listarMesas, qrComoDataUri } from "./api";

// QR Code de UMA mesa, aberto pelos três pontinhos do card dela. Mostra o
// código (com o nome gravado no meio), o link, e as ações só dessa mesa. As
// ações de todas as mesas ficam na janela "QR Codes das mesas".
export default function DialogQrMesa({ mesaId, onFechar, onAbrirTodas }) {
  const [carregando, setCarregando] = useState(true);
  const [mesa, setMesa] = useState(null);
  const [config, setConfig] = useState(null);
  const [imagem, setImagem] = useState(null);

  const carregar = useCallback(async () => {
    if (!mesaId) return;
    setCarregando(true);
    try {
      const { data } = await listarMesas();
      setConfig(data?.config || null);
      const atual = (data?.mesas || []).find((m) => m.id === mesaId) || null;
      setMesa(atual);
      setImagem(atual ? await qrComoDataUri(atual.id) : null);
    } catch {
      toast.error("Não consegui carregar o QR Code desta mesa.");
    } finally {
      setCarregando(false);
    }
  }, [mesaId]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!mesaId) return null;

  const trocarNomeQr = async () => {
    try {
      await gravarNomeQr(mesa.id, mesa.nome);
      toast.success(`O QR agora mostra "${mesa.nome}". Imprima o cartão novo.`);
      carregar();
    } catch {
      toast.error("Não consegui atualizar o nome do QR Code.");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onFechar} data-e2e="dialog-qr-mesa">
      <div className="bg-background rounded-2xl w-full max-w-sm max-h-[92dvh] overflow-y-auto border border-border shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="text-base font-bold truncate">QR Code da {mesa?.nome || "mesa"}</h2>
            <p className="text-xs text-muted-foreground">Imprima e cole nesta mesa.</p>
          </div>
          <button type="button" onClick={onFechar} className="ml-auto p-2 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        {config && !config.enabled ? (
          <div className="mx-5 mt-4 rounded-xl bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs">
            O pedido pela mesa está <strong>desligado</strong>. Ligue em Configurações, Sistema,
            Preferências.
          </div>
        ) : null}

        <div className="p-5 flex flex-col items-center">
          {carregando ? (
            <div className="w-56 h-56 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : imagem ? (
            <img src={imagem} alt={`QR da ${mesa?.nome}`} className="w-56 h-56 rounded-xl bg-white p-2" />
          ) : (
            <p className="text-sm text-muted-foreground py-16">Não consegui gerar o código.</p>
          )}

          {mesa?.nome_qr_diferente ? (
            <div className="mt-3 w-full rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs">
              No meio do QR está <strong>{mesa.nome_qr}</strong>, o nome antigo da mesa.{" "}
              <button type="button" onClick={trocarNomeQr} className="font-bold underline underline-offset-2"
                      data-e2e="qr-trocar-nome">
                Trocar para {mesa.nome}
              </button>
            </div>
          ) : null}

          <LinkDaMesa link={mesa?.link} />
        </div>

        <AcoesQrMesa mesa={mesa} pronto={!!imagem} onMudou={setMesa} onRecarregar={carregar}
                     onAbrirTodas={onAbrirTodas} />
      </div>
    </div>
  );
}
